import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { usePermission, useGlobalPermission } from '@/hooks/usePermission';
import { useOnboarding } from './OnboardingProvider';
import { tourForPath } from './tours';
import { cn } from '@/lib/utils';

/**
 * Coach marks: the second half of the training, and the half that points at things.
 *
 * The welcome dialog explains the *business* — four sections, one Main Safe, money as
 * integers. This explains the *screen*, once, the first time somebody opens it, standing
 * next to the control it is describing.
 *
 * ### Steps are filtered by permission, not merely hidden
 *
 * A step that describes the Main Safe is dropped entirely for a user who cannot see it —
 * it is not greyed out and it does not count toward "3 of 5". Teaching somebody a screen
 * they will never reach is worse than teaching them nothing: it costs their attention and
 * leaves them looking for a menu item that is not there. The check is the same `can()` the
 * navigation uses, so a step and the item it points at cannot disagree.
 *
 * A step whose `target` is not on the page is dropped too. That is a real case — a table
 * with no rows has no row actions — and a spotlight on nothing is a bug the user has to
 * interpret.
 */
export const ScreenTour = () => {
  const { t } = useTranslation(['onboarding', 'common']);
  const { pathname } = useLocation();
  const can = usePermission();
  const canGlobal = useGlobalPermission();
  const { active, finish, offerScreenTour } = useOnboarding();

  const tour = tourForPath(pathname);
  const isOpen = !!tour && active === tour.id;

  const [index, setIndex] = React.useState(0);
  const [rect, setRect] = React.useState(null);

  /*
   * Offer the tour once the route settles.
   *
   * A frame of delay, deliberately: the anchors are `data-tour` attributes on elements
   * this screen is still mounting, and measuring before the paint gives every step a zero
   * rectangle. This is also why the tour is offered from an effect rather than on
   * navigation — the route changes before the screen exists.
   */
  React.useEffect(() => {
    const timer = setTimeout(() => offerScreenTour(pathname), 400);
    return () => clearTimeout(timer);
  }, [pathname, offerScreenTour]);

  React.useEffect(() => {
    if (isOpen) setIndex(0);
  }, [isOpen, tour?.id]);

  const steps = React.useMemo(() => {
    if (!tour) return [];
    return tour.steps.filter((step) => {
      if (step.permission) {
        const allowed = step.global ? canGlobal(step.permission) : can(step.permission);
        if (!allowed) return false;
      }
      // An anchored step with nothing to anchor to is dropped; a narrative one always stays.
      if (step.target && !document.querySelector(step.target)) return false;
      return true;
    });
  }, [tour, can, canGlobal, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = steps[index] ?? null;

  /** Track the anchor through scroll and resize — a spotlight that lags is worse than none. */
  React.useLayoutEffect(() => {
    if (!isOpen || !step?.target) {
      setRect(null);
      return undefined;
    }

    const measure = () => {
      const node = document.querySelector(step.target);
      if (!node) return setRect(null);
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return setRect(node.getBoundingClientRect());
    };

    measure();
    const onChange = () => measure();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [isOpen, step]);

  // Escape ends it, like any overlay.
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') finish(tour.id, 'skip');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, tour, finish]);

  if (!isOpen || !step) return null;

  const isLast = index === steps.length - 1;
  const close = (action) => finish(tour.id, action);

  const PAD = 6;
  const spotlight = rect
    ? {
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60]"
      role="dialog"
      aria-modal="true"
      aria-label={t('onboarding:tour.a11y')}
    >
      {/*
       * The scrim is a single element with a huge ring rather than four positioned divs.
       * `box-shadow` with a spread larger than the viewport dims everything outside the
       * cut-out and needs no arithmetic, so it cannot disagree with the rectangle by a
       * pixel during a scroll.
       */}
      {spotlight ? (
        <div
          className="pointer-events-none absolute rounded-lg ring-2 ring-primary transition-all duration-200"
          style={{ ...spotlight, boxShadow: '0 0 0 9999px hsl(var(--foreground) / 0.55)' }}
        />
      ) : (
        <div className="absolute inset-0 bg-foreground/55" />
      )}

      {/* Clicking the scrim ends the tour, like any overlay. */}
      <button
        type="button"
        aria-label={t('onboarding:actions.skip')}
        className="absolute inset-0 cursor-default"
        onClick={() => close('skip')}
      />

      <div
        className={cn(
          'absolute w-[min(24rem,calc(100vw-2rem))] rounded-xl border bg-popover p-5 shadow-elevated',
          'animate-in fade-in-0 zoom-in-95 duration-200',
        )}
        style={cardPosition(spotlight)}
      >
        <p className="text-caption font-medium uppercase tracking-widest text-muted-foreground">
          {t('onboarding:tour.progress', { step: index + 1, total: steps.length })}
        </p>
        <h3 className="mt-1.5 text-base font-semibold">{t(`onboarding:${step.title}`)}</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`onboarding:${step.body}`)}</p>

        <div className="mt-4 flex items-center justify-between gap-2">
          <Button size="sm" variant="ghost" onClick={() => close('skip')}>
            {t('onboarding:actions.skip')}
          </Button>
          <div className="flex items-center gap-2">
            {index > 0 ? (
              <Button size="sm" variant="outline" onClick={() => setIndex((i) => i - 1)}>
                {t('common:actions.back')}
              </Button>
            ) : null}
            <Button size="sm" onClick={() => (isLast ? close('complete') : setIndex((i) => i + 1))}>
              {isLast ? t('onboarding:actions.done') : t('common:actions.next')}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/**
 * Put the card below the anchor, or above it when there is no room below.
 *
 * Clamped to the viewport on both axes so a step anchored to something near an edge does
 * not open half off-screen — which on a narrow window is most of them.
 */
const cardPosition = (spotlight) => {
  if (!spotlight) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

  const CARD_W = 384;
  const CARD_H = 210;
  const GAP = 12;
  const below = spotlight.top + spotlight.height + GAP;
  const fitsBelow = below + CARD_H < window.innerHeight;

  const top = fitsBelow ? below : Math.max(GAP, spotlight.top - CARD_H - GAP);
  const left = Math.min(Math.max(GAP, spotlight.left), Math.max(GAP, window.innerWidth - CARD_W - GAP));

  return { top, left };
};

export default ScreenTour;
